import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import {
  DollarSign,
  TrendingUp,
  RefreshCw,
  Loader2,
  Download,
  BarChart2,
  PieChart,
  AlertCircle,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { CostSummaryItem } from '../lib/types';

const COLORS = ['#198754', '#0f9d8a', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4'];

export function CostsPage() {
  const { idToken: accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [costData, setCostData] = useState<CostSummaryItem[]>([]);
  const [dailyData, setDailyData] = useState<CostSummaryItem[]>([]);
  const [error, setError] = useState('');
  const [groupBy, setGroupBy] = useState<'service' | 'account' | 'day'>('service');
  const [days, setDays] = useState(30);
  const [selectedAccount, setSelectedAccount] = useState<string | undefined>();

  const fetchCosts = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError('');

    try {
      const [summaryRes, dailyRes] = await Promise.allSettled([
        api.resources.costSummary(accessToken, { group_by: groupBy, days, account_id: selectedAccount }),
        api.resources.costSummary(accessToken, { group_by: 'day', days, account_id: selectedAccount }),
      ]);

      if (summaryRes.status === 'fulfilled') setCostData(summaryRes.value.items);
      if (dailyRes.status === 'fulfilled') setDailyData(dailyRes.value.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load cost data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCosts();
  }, [accessToken, groupBy, days, selectedAccount]);

  const totalCost = useMemo(() => costData.reduce((sum, item) => sum + item.total_usd, 0), [costData]);

  // Backend sorts cost_summary by amount (descending) regardless of group_by - a day-by-day
  // trend needs chronological order, so re-sort by date here.
  const chartData = useMemo(
    () => [...dailyData].sort((a, b) => a.key.localeCompare(b.key)),
    [dailyData],
  );

  const topServices = useMemo(() => costData.slice(0, 6), [costData]);

  if (isLoading && costData.length === 0) {
    return (
      <div className="page-costs">
        <div className="page-header">
          <h1 className="page-title">Cost Explorer</h1>
        </div>
        <div className="card">
          <div className="card-content">
            <div className="empty-state">
              <Loader2 size={48} className="empty-state-icon spin" />
              <h3 className="empty-state-title">Loading cost data...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-costs">
      <header className="page-header">
        <h1 className="page-title">Cost Explorer</h1>
        <div className="page-header-actions">
          <button className="btn btn-ghost btn-icon" onClick={fetchCosts} disabled={isLoading} aria-label="Refresh">
            <RefreshCw size={20} className={isLoading ? 'spin' : ''} />
          </button>
          <button className="btn btn-secondary">
            <Download size={18} /> Export CSV
          </button>
        </div>
      </header>

      {error && (
        <div className="login-alert" style={{ marginBottom: 'var(--space-4)' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Controls */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="card-content" style={{ padding: 'var(--space-4)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'center' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="groupBy" className="form-label">Group By</label>
              <select
                id="groupBy"
                className="form-select"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as 'service' | 'account' | 'day')}
                style={{ minWidth: 160 }}
              >
                <option value="service">Service</option>
                <option value="account">Account</option>
                <option value="day">Day</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="days" className="form-label">Period</label>
              <select
                id="days"
                className="form-select"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                style={{ minWidth: 140 }}
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last 365 days</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
              <label htmlFor="account" className="form-label">Account</label>
              <select
                id="account"
                className="form-select"
                value={selectedAccount || ''}
                onChange={(e) => setSelectedAccount(e.target.value || undefined)}
                style={{ minWidth: 200 }}
              >
                <option value="">All Accounts</option>
                {/* Would be populated from accounts API */}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-stats" style={{ marginBottom: 'var(--space-4)' }} role="list" aria-label="Cost summary">
        <article className="card stat-card" role="listitem">
          <div className="stat-card-header">
            <div className="stat-icon" style={{ backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}>
              <DollarSign size={24} />
            </div>
          </div>
          <div className="stat-card-body">
            <p className="stat-label">Total Cost ({days}d)</p>
            <p className="stat-value">{formatCurrency(totalCost)}</p>
            <p className="stat-sub">{costData.length} {groupBy}(s)</p>
          </div>
        </article>

        <article className="card stat-card" role="listitem">
          <div className="stat-card-header">
            <div className="stat-icon" style={{ backgroundColor: '#e0e7ff', color: '#3730a3' }}>
              <BarChart2 size={24} />
            </div>
          </div>
          <div className="stat-card-body">
            <p className="stat-label">Daily Average</p>
            <p className="stat-value">{formatCurrency(totalCost / Math.max(1, days))}</p>
            <p className="stat-sub">Per day</p>
          </div>
        </article>

        <article className="card stat-card" role="listitem">
          <div className="stat-card-header">
            <div className="stat-icon" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
              <TrendingUp size={24} />
            </div>
          </div>
          <div className="stat-card-body">
            <p className="stat-label">Top Service</p>
            <p className="stat-value">{topServices[0]?.key || '—'}</p>
            <p className="stat-sub">{formatCurrency(topServices[0]?.total_usd || 0)}</p>
          </div>
        </article>

        <article className="card stat-card" role="listitem">
          <div className="stat-card-header">
            <div className="stat-icon" style={{ backgroundColor: '#fce7f3', color: '#be185d' }}>
              <PieChart size={24} />
            </div>
          </div>
          <div className="stat-card-body">
            <p className="stat-label">Projected Monthly</p>
            <p className="stat-value">{formatCurrency((totalCost / Math.max(1, days)) * 30)}</p>
            <p className="stat-sub">Based on current run rate</p>
          </div>
        </article>
      </div>

      {/* Charts Grid */}
      <div className="dashboard-grid">
        {/* Daily Trend */}
        <section className="card" style={{ gridColumn: 'span 2' }} aria-labelledby="trend-heading">
          <header className="card-header">
            <h2 id="trend-heading" className="card-title">Daily Cost Trend</h2>
          </header>
          <div className="card-content" style={{ height: 350 }}>
            {chartData.length === 0 ? (
              <div className="empty-state" style={{ height: '100%' }}>
                <DollarSign size={48} className="empty-state-icon" />
                <h3 className="empty-state-title">No cost data available</h3>
                <p className="empty-state-text">Cost data appears after the first daily sync</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="color-total" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS[0]} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="key"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => `$${value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value.toFixed(2)}`}
                  />
                  <Tooltip
                    formatter={(value: number) => [formatCurrency(value), 'Total cost']}
                    labelFormatter={(date) => new Date(date as string).toLocaleDateString()}
                    contentStyle={{
                      background: 'var(--surface-bg)',
                      border: '1px solid var(--surface-border)',
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-lg)',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="total_usd"
                    stroke={COLORS[0]}
                    fill="url(#color-total)"
                    strokeWidth={2}
                    name="Total cost"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Top Services Bar Chart */}
        <section className="card" aria-labelledby="services-heading">
          <header className="card-header">
            <h2 id="services-heading" className="card-title">Top Services</h2>
          </header>
          <div className="card-content" style={{ height: 350 }}>
            {topServices.length === 0 ? (
              <div className="empty-state" style={{ height: '100%' }}>
                <DollarSign size={48} className="empty-state-icon" />
                <h3 className="empty-state-title">No service data</h3>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topServices} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toFixed(2)}`} />
                  <YAxis type="category" dataKey="key" width={100} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(value: number) => [formatCurrency(value), '']} contentStyle={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)' }} />
                  <Bar dataKey="total_usd" name="Cost" radius={[0, 4, 4, 0]} fill="var(--color-primary)" maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Cost Breakdown Pie */}
        <section className="card" aria-labelledby="pie-heading">
          <header className="card-header">
            <h2 id="pie-heading" className="card-title">Cost Distribution</h2>
          </header>
          <div className="card-content" style={{ height: 350 }}>
            {costData.length === 0 ? (
              <div className="empty-state" style={{ height: '100%' }}>
                <PieChart size={48} className="empty-state-icon" />
                <h3 className="empty-state-title">No data</h3>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={costData.slice(0, 8)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="total_usd"
                    nameKey="key"
                    label={({ key, total_usd, percent }) => `${key}: ${formatCurrency(total_usd)} (${(percent * 100).toFixed(1)}%)`}
                    labelLine={false}
                  >
                    {costData.slice(0, 8).map((_, i) => (
                      <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [formatCurrency(value), '']} contentStyle={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)' }} />
                </RechartsPieChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Cost Table */}
        <section className="card" style={{ gridColumn: 'span 2' }} aria-labelledby="table-heading">
          <header className="card-header">
            <h2 id="table-heading" className="card-title">Cost Breakdown by {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</h2>
          </header>
          <div className="card-content">
            {costData.length === 0 ? (
              <div className="empty-state" style={{ padding: 'var(--space-8)' }}>
                <DollarSign size={48} className="empty-state-icon" />
                <h3 className="empty-state-title">No cost data</h3>
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</th>
                      <th style={{ textAlign: 'right' }}>Cost (USD)</th>
                      <th style={{ textAlign: 'right' }}>% of Total</th>
                      <th style={{ textAlign: 'right' }}>Daily Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costData.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <span className="badge badge-info">{index + 1}</span>
                            <span style={{ fontWeight: 500 }}>{item.key}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                          {formatCurrency(item.total_usd)}
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--slate-600)' }}>
                          {totalCost > 0 ? ((item.total_usd / totalCost) * 100).toFixed(1) : 0}%
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--slate-600)', fontFamily: 'monospace' }}>
                          {formatCurrency(item.total_usd / Math.max(1, days))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}